import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import axios from 'axios';
import { API_BASE_URL, createAuthHeader } from '../../config/api.config';
import { UNITS_RAMP } from '../../pages/Machines/hooks/useHeatmapColors';

const EMPTY = 'rgba(0, 0, 0, 0.05)';

// Same cream → burnt-orange ramp as the Machines planogram heat map.
const rampColor = (t) => {
  const x = Math.max(0, Math.min(1, t));
  for (let i = 1; i < UNITS_RAMP.length; i += 1) {
    const [s1, c1] = UNITS_RAMP[i];
    const [s0, c0] = UNITS_RAMP[i - 1];
    if (x <= s1) {
      const k = (x - s0) / (s1 - s0 || 1);
      return `rgb(${c0.map((v, j) => Math.round(v + (c1[j] - v) * k)).join(',')})`;
    }
  }
  return `rgb(${UNITS_RAMP[UNITS_RAMP.length - 1][1].join(',')})`;
};

/**
 * A machine's planogram, small: one cell per lane with its product name, coloured by last month's units
 * (a product's units split over its lanes). The lanes of `highlightProductId` are outlined, those of
 * the `pickedLaneId` lane in green. Clicking any other lane hands its product and lane to `onPick`. With `onToggleLane`,
 * clicking one of `highlightProductId`'s lanes hands its selection id (and all of them) to it: the lanes in
 * `chosenLaneIds` (null = all of them) stay outlined, the others dashed and faded. With `months`, hovering a lane
 * hands `onHoverLane` that lane's history over the last `months` months — its product's units sold from the lane, the
 * days it was in the lane and the days the machine had a snapshot — and the machine's best lane-month, the one scale
 * every lane shares (null on leaving). The history comes in a second, slower call: until then a hover says it loads.
 */
const MiniPlanogram = ({
  clientId, storeId, highlightProductId, pickedLaneId, chosenLaneIds = null, onReady, onPick, onToggleLane,
  months, onHoverLane, cellHeight = 30,
}) => {
  const [slots, setSlots] = useState(null);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const headers = createAuthHeader(localStorage.getItem('access_token'));
    const url = `${API_BASE_URL}/metrics/store/${storeId}/planogram?clientId=${encodeURIComponent(clientId)}`;
    axios.get(url, { headers })
      .then(({ data }) => { if (!cancelled) { setSlots(data.slots || []); onReady?.(); } })
      .catch(() => { if (!cancelled) setSlots([]); });
    if (months) {
      axios.get(`${url}&months=${months}&lanes=1`, { headers })
        .then(({ data }) => { if (!cancelled && data.monthly?.lanes) setHistory(data.monthly); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [clientId, storeId, months]); // eslint-disable-line react-hooks/exhaustive-deps

  const scaleMax = useMemo(
    () => (history ? Math.max(1, ...Object.values(history.lanes).flatMap((lane) => lane.units)) : 1),
    [history]
  );

  const grid = useMemo(() => {
    if (!slots?.length) return null;
    const lanes = new Map();
    slots.forEach((s) => lanes.set(s.productId, (lanes.get(s.productId) || 0) + 1));
    const laneUnits = (s) => (s.productId == null ? 0 : s.unitsLastMonth / (lanes.get(s.productId) || 1));
    const max = Math.max(1, ...slots.map(laneUnits));
    return {
      rows: Math.max(...slots.map((s) => s.row)),
      columns: Math.max(...slots.map((s) => s.column)),
      byKey: new Map(slots.map((s) => [`${s.row}-${s.column}`, { ...s, laneUnits: laneUnits(s), heat: laneUnits(s) / max }])),
    };
  }, [slots]);

  if (!slots) return <Box sx={{ py: 1, display: 'flex', justifyContent: 'center' }}><CircularProgress size={14} /></Box>;
  if (!grid) return null;
  const ownLaneIds = slots.filter((s) => String(s.productId) === String(highlightProductId)).map((s) => s.selectionId);

  const cells = [];
  for (let r = 1; r <= grid.rows; r += 1) {
    for (let c = 1; c <= grid.columns; c += 1) {
      const s = grid.byKey.get(`${r}-${c}`);
      if (!s) {
        cells.push(<Box key={`${r}-${c}`} sx={{ height: cellHeight, borderRadius: '3px', backgroundColor: EMPTY }} />);
        continue;
      }
      const own = String(s.productId) === String(highlightProductId);
      const chosen = own && (chosenLaneIds == null || chosenLaneIds.includes(s.selectionId));
      const picked = pickedLaneId != null && String(s.selectionId) === String(pickedLaneId);
      const pickable = onPick && s.productId != null && !own;
      const toggleable = own && onToggleLane;
      cells.push(
        <Box
          key={`${r}-${c}`}
          onClick={pickable ? () => onPick({ id: s.productId, name: s.name, selectionId: s.selectionId })
            : toggleable ? () => onToggleLane(s.selectionId, ownLaneIds) : undefined}
          onMouseEnter={onHoverLane && months ? () => {
            const lane = history?.lanes[`${s.selectionId}:${s.productId}`];
            onHoverLane({
              productId: s.productId, name: s.name, selectionId: s.selectionId, loading: !history,
              months: history?.months, snapshotDays: history?.snapshotDays, scaleMax,
              series: lane?.units ?? history?.months.map(() => 0), days: lane?.days ?? history?.months.map(() => 0),
            });
          } : undefined}
          sx={{
            height: cellHeight,
            px: '3px',
            borderRadius: '3px',
            backgroundColor: rampColor(s.heat),
            outline: chosen ? '2px solid #4f46e5' : own ? '2px dashed #a5b4fc' : picked ? '2px solid #15803d' : 'none',
            outlineOffset: '-1px',
            opacity: own && !chosen ? 0.55 : 1,
            cursor: pickable || toggleable ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            '&:hover': pickable || toggleable ? { filter: 'brightness(0.92)' } : undefined,
          }}
        >
          <Typography sx={{
            fontSize: '8.5px',
            lineHeight: 1.15,
            color: s.heat > 0.6 ? '#fff' : '#3f3f46',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
          }}>
            {s.name || '—'}
          </Typography>
        </Box>
      );
    }
  }
  return (
    <Box
      onMouseLeave={onHoverLane ? () => onHoverLane(null) : undefined}
      sx={{ display: 'grid', gridTemplateColumns: `repeat(${grid.columns}, minmax(0, 1fr))`, gap: '3px', py: 0.5 }}
    >
      {cells}
    </Box>
  );
};

export default MiniPlanogram;
